/**
 * Chip Component - Controller
 * =============================
 */

import type { ISCEService, ITranscludeFunction } from 'angular';
import { hasTranscludedContent } from '../../_base/transclusion';
import {
  INTENT_EMPHASIS,
  SIZES,
  BASE_CLASSES,
  ICONS,
  DISMISS_ICON,
  DEFAULTS,
  ChipIntent,
  ChipEmphasis,
  ChipSize,
  ChipIconType
} from './constants';

export interface ChipBindings {
  text?: string;
  intent?: ChipIntent;
  emphasis?: ChipEmphasis;
  size?: ChipSize;
  dismissible?: boolean;
  showIcon?: boolean;
  icon?: ChipIconType;
  onDismiss?: (args: { $event: Event }) => void;
}

interface BindingChanges {
  icon?: { currentValue: ChipIconType };
  [key: string]: unknown;
}

export class ChipController implements ChipBindings {
  static $inject = ['$sce', '$transclude'];

  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  // Bindings
  text?: string;
  intent?: ChipIntent;
  emphasis?: ChipEmphasis;
  size?: ChipSize;
  dismissible?: boolean;
  showIcon?: boolean;
  icon?: ChipIconType;
  onDismiss?: (args: { $event: Event }) => void;

  // Estado interno (SVG confiables para ng-bind-html)
  trustedIcon: unknown = null;
  trustedDismissIcon: unknown = null;

  // Contenido transcluido reemplaza el binding "text" (mismo patrón que Button)
  hasContent = false;

  constructor($sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$sce = $sce;
    this.$transclude = $transclude;
    this.handleDismiss = this.handleDismiss.bind(this);
  }

  $onInit(): void {
    this.intent = this.intent || DEFAULTS.intent;
    this.emphasis = this.emphasis || DEFAULTS.emphasis;
    this.size = this.size || DEFAULTS.size;
    this.icon = this.icon || DEFAULTS.icon;
    if (this.dismissible === undefined || this.dismissible === null) {
      this.dismissible = true;
    }
    this.hasContent = hasTranscludedContent(this.$transclude);
    this._updateTrustedIcon();
    if (this.$sce) {
      this.trustedDismissIcon = this.$sce.trustAsHtml(DISMISS_ICON);
    }
  }

  $onChanges(changes: BindingChanges): void {
    if (changes.icon) {
      this._updateTrustedIcon();
    }
  }

  $onDestroy(): void {
    this.$sce = null;
    this.$transclude = null;
  }

  private _updateTrustedIcon(): void {
    if (!this.$sce) return;
    this.trustedIcon = this.$sce.trustAsHtml(ICONS[this.icon || DEFAULTS.icon] || '');
  }

  // ---- Eventos ----

  handleDismiss($event: Event): void {
    $event.stopPropagation();
    if (typeof this.onDismiss === 'function') {
      this.onDismiss({ $event });
    }
  }

  // ---- Clases ----

  getChipClasses(): string[] {
    const intent = this.intent || DEFAULTS.intent;
    const emphasis = this.emphasis || DEFAULTS.emphasis;
    const size = SIZES[this.size || DEFAULTS.size];

    const classes = [BASE_CLASSES.chip, size.container, size.gap];
    classes.push(emphasis === 'neutral' ? BASE_CLASSES.neutral : INTENT_EMPHASIS[intent][emphasis]);
    return classes;
  }

  getIconClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].icon];
  }

  getLabelClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].label];
  }

  getDismissClasses(): string[] {
    return [BASE_CLASSES.dismiss, SIZES[this.size || DEFAULTS.size].icon];
  }
}

export default ChipController;
